import type { ReactNode } from 'react';
import type { SxProps, Theme } from '@mui/material/styles';
import Alert from '@mui/material/Alert';
import AlertTitle from '@mui/material/AlertTitle';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import Typography from '@mui/material/Typography';
import RefreshIcon from '@mui/icons-material/Refresh';
import { FormattedMessage } from 'react-intl';
import type { LoadFailure } from './loadFailure';

type FailureKind =
    'network' | 'unavailable' | 'forbidden' | 'server' | 'refused';

/**
 * What the status says about waiting: a lost line or a restarting server
 * passes by itself, a refusal or a fault on the server does not.
 */
function kindOf(status: number | null): FailureKind {
    if (status === null) {
        return 'network';
    }
    if (status === 401 || status === 403) {
        return 'forbidden';
    }
    if (status === 502 || status === 503 || status === 504) {
        return 'unavailable';
    }
    return status >= 500 ? 'server' : 'refused';
}

/**
 * The one way a screen says that something it reads from the server did not
 * arrive. It sits ABOVE what it is about and offers to try again.
 *
 * The status decides the rest. A passing hitch is a calm warning with no
 * technical detail; a lasting one is an error that names the server's answer,
 * because someone will have to pass it on. A missing permission offers no
 * retry — trying again would only be refused again.
 */
export default function LoadErrorAlert({
    title,
    failure,
    onRetry,
    retrying = false,
    sx
}: {
    /** What did not load, as a whole sentence: "Der Verlauf lässt sich …". */
    title: ReactNode;
    failure: LoadFailure;
    onRetry?: () => void;
    /** Keeps the alert standing with a spinning button while the retry runs. */
    retrying?: boolean;
    sx?: SxProps<Theme>;
}) {
    const kind = kindOf(failure.status);
    const passing = kind === 'network' || kind === 'unavailable';
    const canRetry = onRetry && kind !== 'forbidden';

    return (
        <Alert
            severity={passing ? 'warning' : 'error'}
            sx={sx}
            action={
                canRetry ? (
                    <Button
                        color="inherit"
                        size="small"
                        disabled={retrying}
                        startIcon={
                            retrying ? (
                                <CircularProgress size={16} color="inherit" />
                            ) : (
                                <RefreshIcon />
                            )
                        }
                        onClick={onRetry}
                    >
                        <FormattedMessage id="loadError.retry" />
                    </Button>
                ) : undefined
            }
        >
            <AlertTitle>{title}</AlertTitle>
            <FormattedMessage id={`loadError.hint.${kind}`} />
            {!passing && failure.status !== null && (
                <Typography
                    variant="caption"
                    component="div"
                    sx={{ mt: 0.5, opacity: 0.85 }}
                >
                    {failure.reason ? (
                        <FormattedMessage
                            id="loadError.detail"
                            values={{
                                reason: failure.reason,
                                status: failure.status
                            }}
                        />
                    ) : (
                        <FormattedMessage
                            id="loadError.detailStatus"
                            values={{ status: failure.status }}
                        />
                    )}
                </Typography>
            )}
        </Alert>
    );
}
